import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, ChevronDown, Check } from 'lucide-react';
import Footer from '../components/Footer';
import { FairViewsBadge } from '../components/FairViewsWidgets';
import PromoCodeInput from '../components/PromoCodeInput';
import { navCategories } from '../mockData';
import { useCart } from '../context/CartContext';
import Logo from '../components/Logo';
import axios from 'axios';
import { formatPrice } from '../utils/priceFormatter';

const CheckoutPage = () => {
  console.log('🚀 CheckoutPage component is mounting/rendering');
  const navigate = useNavigate();
  const { cartItems, getCartTotal } = useCart();
  const [step, setStep] = useState(1);
  
  const [isProfessional, setIsProfessional] = useState(false);
  const [useDifferentBillingAddress, setUseDifferentBillingAddress] = useState(false);
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  console.log('BACKEND_URL in CheckoutPage:', BACKEND_URL);

  useEffect(() => {
    fetchDeliveryMethods();
  }, []);

  const fetchDeliveryMethods = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/delivery/methods`);
      const data = response.data;
      setDeliveryMethods(data);
      // Set first method as default
      if (data.length > 0) {
        setSelectedDeliveryMethod(data[0]);
        setFormData(prev => ({ ...prev, deliveryMethod: data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching delivery methods:', error);
    }
  };
  
  const [formData, setFormData] = useState({
    civility: 'M',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Professional fields
    companyName: '',
    siret: '',
    vatNumber: '',
    // Delivery address
    address: '',
    addressComplement: '',
    postalCode: '',
    city: '',
    country: 'France',
    // Billing address
    billingAddress: '',
    billingAddressComplement: '',
    billingPostalCode: '',
    billingCity: '',
    billingCountry: 'France',
    deliveryMethod: 'standard',
    paymentMethod: 'card',
    acceptTerms: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setSubmitError(null);
    
    // Validate form
    if (!formData.acceptTerms) {
      setSubmitError('Veuillez accepter les conditions générales de vente');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Starting order submission...');
      console.log('Backend URL:', BACKEND_URL);
      console.log('FormData:', formData);
      console.log('FormData.email:', formData.email);
      console.log('FormData.firstName:', formData.firstName);
      
      // Create order in database first
      const orderData = {
        id: `order_${Date.now()}`,
        customer: {
          civility: formData.civility,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          isProfessional: isProfessional,
          companyName: isProfessional ? formData.companyName : null,
          siret: isProfessional ? formData.siret : null,
          vatNumber: isProfessional ? formData.vatNumber : null
        },
        deliveryAddress: {
          address: formData.address,
          addressComplement: formData.addressComplement,
          postalCode: formData.postalCode,
          city: formData.city,
          country: formData.country
        },
        billingAddress: useDifferentBillingAddress ? {
          address: formData.billingAddress,
          addressComplement: formData.billingAddressComplement,
          postalCode: formData.billingPostalCode,
          city: formData.billingCity,
          country: formData.billingCountry
        } : {
          address: formData.address,
          addressComplement: formData.addressComplement,
          postalCode: formData.postalCode,
          city: formData.city,
          country: formData.country
        },
        items: cartItems.map(item => ({
          productId: item.product.id,
          productName: item.product.name,
          configuration: item.configuration,
          quantity: item.configuration.quantity,
          unitPrice: item.totalPrice / item.configuration.quantity,
          totalPrice: item.totalPrice
        })),
        deliveryMethod: selectedDeliveryMethod ? {
          id: selectedDeliveryMethod.id,
          name: selectedDeliveryMethod.name,
          price: selectedDeliveryMethod.base_price_ttc || selectedDeliveryMethod.price_ttc || 0
        } : null,
        paymentMethod: formData.paymentMethod,
        subtotal: calculateCartSubtotal(),
        deliveryCost: calculateDeliveryCost(),
        promoCode: appliedPromo ? {
          code: appliedPromo.code,
          discount_type: appliedPromo.discount_type,
          discount_value: appliedPromo.discount_value,
          discount_amount: appliedPromo.discount_amount
        } : null,
        totalTTC: calculateGrandTotal(),
        status: formData.paymentMethod === 'card' ? 'pending_payment' : 'pending_transfer',
        payment_status: 'pending',
        createdAt: new Date().toISOString()
      };
      
      console.log('Submitting order to:', `${BACKEND_URL}/api/orders/`);
      
      // Save order to backend using axios (avoids Response cloning issues)
      let savedOrder;
      try {
        const orderResponse = await axios.post(`${BACKEND_URL}/api/orders/`, orderData, {
          headers: { 'Content-Type': 'application/json' }
        });
        savedOrder = orderResponse.data;
        console.log('Order created successfully:', savedOrder);
      } catch (error) {
        console.error('Order creation error:', error.response?.data);
        let errorMessage = 'Erreur lors de la création de la commande';
        
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            if (Array.isArray(error.response.data.detail)) {
              errorMessage = error.response.data.detail.map(err => err.msg || err.message).join(', ');
            } else {
              errorMessage = error.response.data.detail;
            }
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
      }
      
      // If payment method is card, redirect to Stripe
      if (formData.paymentMethod === 'card') {
        console.log('Creating Stripe checkout session...');
        
        // Create Stripe checkout session
        const stripeData = {
          order_id: savedOrder.id || orderData.id,
          amount: calculateGrandTotal(),
          currency: 'eur',
          metadata: {
            customer_email: formData.email,
            customer_name: `${formData.firstName} ${formData.lastName}`
          },
          origin_url: window.location.origin
        };
        
        console.log('Stripe session data:', stripeData);
        console.log('Calling:', `${BACKEND_URL}/api/payments/stripe/create-checkout-session`);
        
        // Create Stripe session using axios (avoids Response cloning issues)
        let stripeSession;
        try {
          const stripeResponse = await axios.post(
            `${BACKEND_URL}/api/payments/stripe/create-checkout-session`,
            stripeData,
            { headers: { 'Content-Type': 'application/json' } }
          );
          stripeSession = stripeResponse.data;
          console.log('Stripe session created:', stripeSession);
        } catch (error) {
          console.error('Stripe session error:', error.response?.data);
          let errorMessage = 'Erreur lors de la création de la session de paiement';
          
          if (error.response?.data) {
            if (typeof error.response.data === 'string') {
              errorMessage = error.response.data;
            } else if (error.response.data.detail) {
              errorMessage = error.response.data.detail;
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.data.error) {
              errorMessage = error.response.data.error;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          throw new Error(errorMessage);
        }
        
        
        // Redirect to Stripe
        console.log('Redirecting to Stripe:', stripeSession.url);
        window.location.href = stripeSession.url;
        
      } else if (formData.paymentMethod === 'transfer') {
        // For bank transfer, show confirmation page
        navigate('/order-confirmation', { state: { order: savedOrder } });
      }
      
    } catch (error) {
      console.error('Error submitting order:', error);
      setSubmitError(`Erreur: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  // Calculate cart totals
  const calculateCartSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const calculateCartSubtotalHT = () => {
    const subtotalTTC = calculateCartSubtotal();
    return subtotalTTC / 1.20; // Remove 20% TVA
  };

  const calculateCartTVA = () => {
    return calculateCartSubtotal() - calculateCartSubtotalHT();
  };

  const calculateDeliveryCost = () => {
    if (!selectedDeliveryMethod) return 0;
    
    // Calculer le poids total du panier
    const totalWeight = cartItems.reduce((sum, item) => {
      const weight = item.configuration?.fabricCalculation?.poids_total || 0;
      return sum + (weight / 1000); // Convertir grammes en kg
    }, 0);
    
    // Formule: Prix de base + (Prix au kg × Poids)
    const baseCost = selectedDeliveryMethod.base_price_ttc || 0;
    const perKgCost = (selectedDeliveryMethod.price_per_kg_ttc || 0) * totalWeight;
    
    return baseCost + perKgCost;
  };

  const calculateGrandTotal = () => {
    const cartTotal = calculateCartSubtotal();
    const deliveryCost = calculateDeliveryCost();
    let total = cartTotal + deliveryCost;
    
    // Apply promo code discount if applicable
    if (appliedPromo) {
      total = total - appliedPromo.discount_amount;
    }
    
    return Math.max(0, total);
  };

  // Redirect if cart is empty (disabled temporarily to debug)
  // useEffect(() => {
  //   // Wait a bit for cart to load from localStorage before redirecting
  //   const timer = setTimeout(() => {
  //     console.log('🔍 CheckoutPage - useEffect checking cart:', cartItems.length, 'items');
  //     console.log('🔍 CheckoutPage - Cart contents:', JSON.stringify(cartItems));
  //     if (cartItems.length === 0) {
  //       console.log('⚠️ CheckoutPage - Cart is empty after delay, redirecting to /panier');
  //       navigate('/panier');
  //     } else {
  //       console.log('✅ CheckoutPage - Cart has items, staying on page');
  //     }
  //   }, 500); // Wait 500ms for cart to load
    
  //   return () => clearTimeout(timer);
  // }, [cartItems, navigate]);
  
  // Log cart state for debugging
  useEffect(() => {
    console.log('🔍 CheckoutPage - Cart state:', {
      itemsCount: cartItems.length,
      items: cartItems
    });
  }, [cartItems]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-8">
            <Logo showTagline={true} clickable={true} />

            <div className="flex items-center gap-6">
              <button className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
                <User size={24} />
              </button>
              <button onClick={() => navigate('/panier')} className="flex items-center gap-2 text-gray-700 hover:text-gray-900 relative">
                <ShoppingCart size={24} />
                <span className="absolute -top-2 -right-2 bg-celadon text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  1
                </span>
                <span className="font-medium">PANIER</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-celadon text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > 1 ? <Check size={20} /> : '1'}
              </div>
              <span className="ml-2 text-sm font-medium">Informations</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 mx-4"></div>
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-celadon text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > 2 ? <Check size={20} /> : '2'}
              </div>
              <span className="ml-2 text-sm font-medium">Livraison</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 mx-4"></div>
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-celadon text-white' : 'bg-gray-200 text-gray-500'}`}>
                3
              </div>
              <span className="ml-2 text-sm font-medium">Paiement</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-3 gap-8">
          {/* Form */}
          <div className="col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
              {step === 1 && (
                <div>
                  <h2 className="text-2xl font-bold mb-6">Informations personnelles</h2>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Civilité *</label>
                      <select
                        name="civility"
                        value={formData.civility}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        required
                      >
                        <option value="M">M.</option>
                        <option value="Mme">Mme</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Prénom *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Nom *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Téléphone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        required
                      />
                    </div>
                  </div>

                  {/* Professional Checkbox */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isProfessional}
                        onChange={(e) => setIsProfessional(e.target.checked)}
                        className="w-4 h-4 text-celadon border-gray-300 rounded focus:ring-[#2ECC71]"
                      />
                      <span className="ml-2 text-sm font-medium">Je suis un professionnel</span>
                    </label>
                  </div>

                  {/* Professional Fields */}
                  {isProfessional && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h3 className="text-lg font-bold mb-4 text-blue-900">Informations professionnelles</h3>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Raison sociale *</label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                          required={isProfessional}
                          placeholder="Nom de votre entreprise"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Numéro SIRET *</label>
                          <input
                            type="text"
                            name="siret"
                            value={formData.siret}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                            required={isProfessional}
                            placeholder="123 456 789 00012"
                            pattern="[0-9\s]{14,17}"
                            title="14 chiffres (espaces autorisés)"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">N° TVA intracommunautaire *</label>
                          <input
                            type="text"
                            name="vatNumber"
                            value={formData.vatNumber}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                            required={isProfessional}
                            placeholder="FR12345678901"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <h3 className="text-xl font-bold mb-4 mt-8">Adresse de livraison</h3>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Adresse *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Complément d'adresse</label>
                    <input
                      type="text"
                      name="addressComplement"
                      value={formData.addressComplement}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Code postal *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Ville *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Pays *</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                      required
                    >
                      <option value="France">France</option>
                      <option value="Belgique">Belgique</option>
                      <option value="Suisse">Suisse</option>
                      <option value="Luxembourg">Luxembourg</option>
                    </select>
                  </div>

                  {/* Billing Address Checkbox */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useDifferentBillingAddress}
                        onChange={(e) => setUseDifferentBillingAddress(e.target.checked)}
                        className="w-4 h-4 text-celadon border-gray-300 rounded focus:ring-[#2ECC71]"
                      />
                      <span className="ml-2 text-sm font-medium">
                        Utiliser une adresse de facturation différente
                      </span>
                    </label>
                  </div>

                  {/* Billing Address Fields */}
                  {useDifferentBillingAddress && (
                    <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <h3 className="text-xl font-bold mb-4 text-yellow-900">Adresse de facturation</h3>

                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Adresse *</label>
                        <input
                          type="text"
                          name="billingAddress"
                          value={formData.billingAddress}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                          required={useDifferentBillingAddress}
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Complément d'adresse</label>
                        <input
                          type="text"
                          name="billingAddressComplement"
                          value={formData.billingAddressComplement}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Code postal *</label>
                          <input
                            type="text"
                            name="billingPostalCode"
                            value={formData.billingPostalCode}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                            required={useDifferentBillingAddress}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Ville *</label>
                          <input
                            type="text"
                            name="billingCity"
                            value={formData.billingCity}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                            required={useDifferentBillingAddress}
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Pays *</label>
                        <select
                          name="billingCountry"
                          value={formData.billingCountry}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                          required={useDifferentBillingAddress}
                        >
                          <option value="France">France</option>
                          <option value="Belgique">Belgique</option>
                          <option value="Suisse">Suisse</option>
                          <option value="Luxembourg">Luxembourg</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full py-3 bg-celadon text-white rounded font-medium hover:bg-celadon-light transition-colors"
                  >
                    Continuer vers la livraison
                  </button>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="text-2xl font-bold mb-6">Mode de livraison</h2>

                  <div className="space-y-4 mb-6">
                    {deliveryMethods.map((method) => (
                      <label 
                        key={method.id}
                        className={`flex items-start gap-4 p-4 border-2 rounded cursor-pointer transition-colors ${
                          formData.deliveryMethod === method.id 
                            ? 'border-celadon bg-green-50' 
                            : 'border-gray-300 hover:border-celadon'
                        }`}
                      >
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value={method.id}
                          checked={formData.deliveryMethod === method.id}
                          onChange={(e) => {
                            handleInputChange(e);
                            setSelectedDeliveryMethod(method);
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">{method.name}</span>
                            <div className="text-right">
                              {isProfessional ? (
                                <div>
                                  <span className="font-bold text-gray-900">{formatPrice(method.base_price_ht)} € HT (base)</span>
                                  <div className="text-xs text-gray-600">+ {formatPrice(method.price_per_kg_ht)} € HT/kg</div>
                                </div>
                              ) : (
                                <div>
                                  <span className="font-bold text-gray-900">{formatPrice(method.base_price_ttc)} €</span>
                                  <span className="text-sm text-gray-600"> + {formatPrice(method.price_per_kg_ttc)} €/kg</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600">{method.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 py-3 border border-gray-300 text-gray-700 rounded font-medium hover:bg-gray-50 transition-colors"
                    >
                      Retour
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="flex-1 py-3 bg-celadon text-white rounded font-medium hover:bg-celadon-light transition-colors"
                    >
                      Continuer vers le paiement
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="text-2xl font-bold mb-6">Mode de paiement</h2>

                  <div className="space-y-4 mb-6">
                    <label className="flex items-start gap-4 p-4 border-2 border-gray-300 rounded cursor-pointer hover:border-celadon transition-colors">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={formData.paymentMethod === 'card'}
                        onChange={handleInputChange}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Carte bancaire</span>
                        <p className="text-sm text-gray-600 mt-1">Paiement sécurisé par CB</p>
                      </div>
                    </label>

                    <label className="flex items-start gap-4 p-4 border-2 border-gray-300 rounded cursor-pointer hover:border-celadon transition-colors">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="transfer"
                        checked={formData.paymentMethod === 'transfer'}
                        onChange={handleInputChange}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Virement bancaire</span>
                        <p className="text-sm text-gray-600 mt-1">Commande traitée après réception du virement</p>
                      </div>
                    </label>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={handleInputChange}
                        className="mt-1"
                        required
                      />
                      <span className="text-sm text-gray-700">
                        J'accepte les <a href="#" className="text-celadon hover:underline">conditions générales de vente</a> et 
                        la <a href="#" className="text-celadon hover:underline">politique de confidentialité</a>
                      </span>
                    </label>
                  </div>

                  <div className="flex gap-4">
                    {submitError && (
                      <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
                        {submitError}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="flex-1 py-3 border border-gray-300 text-gray-700 rounded font-medium hover:bg-gray-50 transition-colors"
                      disabled={isSubmitting}
                    >
                      Retour
                    </button>
                    <button
                      type="submit"
                      disabled={!formData.acceptTerms || isSubmitting}
                      className={`flex-1 py-3 rounded font-medium transition-colors ${
                        formData.acceptTerms && !isSubmitting
                          ? 'bg-celadon text-white hover:bg-celadon-light'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      }`}
                    >
                      {isSubmitting ? 'Traitement en cours...' : 'Valider ma commande'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h3 className="text-xl font-bold mb-4">Récapitulatif de commande</h3>

              <div className="space-y-3 mb-6">
                {/* List cart items */}
                <div className="mb-4 pb-4 border-b">
                  <h3 className="font-bold text-gray-900 mb-3">Vos articles ({cartItems.length})</h3>
                  {cartItems.map((item, index) => {
                    // Generate thumbnail path from image if thumbnail doesn't exist
                    const getThumbnailPath = (product) => {
                      if (product.thumbnail) {
                        return product.thumbnail;
                      }
                      // Generate thumbnail path from image
                      if (product.image && product.image.startsWith('/images/')) {
                        const base = product.image.substring(0, product.image.lastIndexOf('.'));
                        const ext = product.image.substring(product.image.lastIndexOf('.'));
                        return `${base}_thumb${ext}`;
                      }
                      return product.image;
                    };
                    
                    return (
                    <div key={index} className="flex gap-3 mb-3 text-sm">
                      <img 
                        src={getThumbnailPath(item.product)}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded border"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.product.name}</p>
                        <p className="text-xs text-gray-600">
                          {item.configuration.largeur} × {item.configuration.projection} cm
                        </p>
                        <p className="text-xs text-gray-600">Quantité: {item.configuration.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(item.totalPrice)} €</p>
                      </div>
                    </div>
                    );
                  })}
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total produits {isProfessional ? 'HT' : 'TTC'}</span>
                  <span className="font-medium">
                    {isProfessional 
                      ? `${formatPrice(calculateCartSubtotalHT())} €` 
                      : `${formatPrice(calculateCartSubtotal())} €`}
                  </span>
                </div>
                
                {isProfessional && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">TVA produits (20%)</span>
                    <span className="font-medium">{formatPrice(calculateCartTVA())} €</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Livraison {isProfessional && selectedDeliveryMethod ? 'HT' : ''}</span>
                  <span className="font-medium">
                    {selectedDeliveryMethod ? (
                      isProfessional 
                        ? `${formatPrice(calculateDeliveryCost() / 1.20)} €` 
                        : `${formatPrice(calculateDeliveryCost())} €`
                    ) : '-'}
                  </span>
                </div>
                
                {isProfessional && selectedDeliveryMethod && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">TVA livraison (20%)</span>
                    <span className="font-medium">{formatPrice(calculateDeliveryCost() - (calculateDeliveryCost() / 1.20))} €</span>
                  </div>
                )}

                {/* Promo Code Section */}
                <div className="pt-3 border-t">
                  <PromoCodeInput
                    orderTotal={calculateCartSubtotal() + calculateDeliveryCost()}
                    customerEmail={formData.email}
                    onPromoApplied={setAppliedPromo}
                    appliedPromo={appliedPromo}
                  />
                </div>

                {/* Show discount line if promo applied */}
                {appliedPromo && (
                  <div className="flex justify-between text-sm text-green-600 mt-2">
                    <span>Réduction ({appliedPromo.code})</span>
                    <span className="font-medium">-{formatPrice(appliedPromo.discount_amount)} €</span>
                  </div>
                )}
                
                <div className="pt-3 border-t">
                  <div className="flex justify-between text-lg">
                    <span className="font-bold">Total TTC</span>
                    <span className="font-bold">{formatPrice(calculateGrandTotal())} €</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded text-sm text-gray-700">
                <p className="mb-2">🔒 Paiement 100% sécurisé</p>
                <p>Vos données sont cryptées et protégées</p>
              </div>

              {/* FairViews Badge - Avis clients */}
              <div className="mt-4 flex justify-center">
                <FairViewsBadge />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
